<template>
  <se-modal
    :active="registerModalActive"
    :disable-close="registering"
    title="loyalty.register"
    class="text-dark-gray"
    max-width="max-w-sm sm:max-w-2xl xl:max-w-3xl"
    @keyup.enter="initSubmitLoyaltyForm()"
    @close="loyaltyStore.toggleRegisterModal(false)"
  >
    <div class="flex flex-col gap-3">
      <transition name="slide-fade">
        <se-alert v-if="errorMessage" type="danger">
          <template #desc>
            <p class="text-sm sm:text-base">{{ errorMessage }}</p>
          </template>
        </se-alert>
      </transition>
      <transition name="slide-fade">
        <se-alert v-if="confirmationMessage" type="info">
          <template #desc>
            <p class="text-sm sm:text-base">{{ confirmationMessage }}</p>
          </template>
        </se-alert>
      </transition>
      <div>{{ $t('loyalty.registerEnterDetails') }}</div>
      <form-kit
        id="loyalty-register-form"
        type="form"
        :disabled="registering"
        :actions="false"
        @submit="(data) => registerSubmitHandler(data)"
      >
        <form-kit-schema :schema="LoyaltyRegisterForm" :data="data" />
      </form-kit>

      <div class="flex items-center justify-between gap-x-3 sm:justify-end">
        <se-button class="sm:w-fit" :disabled="registering" :spinner="registering" @click="initSubmitLoyaltyForm()">
          {{ $t('loyalty.register') }}
        </se-button>
        <se-button class="sm:w-fit" color="gray" :disabled="registering" @click="toggleRegisterModal(false)">
          {{ $t('shared.cancel') }}
        </se-button>
      </div>
    </div>
  </se-modal>
</template>

<script setup lang="ts">
import { FormKit, FormKitSchema, submitForm } from '@formkit/vue';
import LoyaltyRegisterForm from '@f/schema/loyalty-register-form';
import { useLoyaltyStore } from '@f/store/loyaltyStore';
import countries from '@s/assets/lists/countries';
import { SADCCountries } from '@s/assets/lists/sadc';
import { isValidPensioner, isValidSADCId } from '@f/helpers/id-validators';

const registering = ref(false);
const errorMessage = ref('');
const confirmationMessage = ref('');
const loyaltyStore = useLoyaltyStore();
const { registerModalActive } = storeToRefs(loyaltyStore);

const clearError = () => {
  errorMessage.value = '';
};

const toggleRegisterModal = (active: boolean) => {
  registerModalActive.value = active;

  confirmationMessage.value = '';
  clearError();
};

const initSubmitLoyaltyForm = () => {
  submitForm('loyalty-register-form');
};

const isSADCMember = (countryName: string) => {
  return SADCCountries.includes(countryName);
};

const registerSubmitHandler = async (formData: {
  first_name: string
  last_name: string
  email: string
  password: string
  country: string
  identity_number?: string
}) => {
  try {
    clearError();
    registering.value = true;

    const registerData = {
      ...formData,
      sadc_member: isSADCMember(formData.country),
      pensioner: formData.identity_number ? isValidPensioner(formData.identity_number) : false,
    };

    const res = await loyaltyStore.register(registerData);

    if (res?.token) {
      toggleRegisterModal(false);
    }
    else {
      // Same message whether or not the email was already registered; the next step
      // is in the inbox.
      confirmationMessage.value = res?.message ?? 'Check your email to finish setting up your account.';
    }
  } catch (error: any) {
    errorMessage.value = error.message;
  } finally {
    registering.value = false;
  }
};

const data = reactive({
  countries,
  isSADCMember,
  validationRules: { isValidSADCId },
});
</script>
